import React, { useState, useEffect } from "react";
import { Card, Typography, Steps, List, Collapse, Row, Col, Tag, Image, message } from "antd";
import AWS from "aws-sdk";

const { Title, Paragraph, Link, Text } = Typography;
const { Panel } = Collapse;

function AboutProcess() {
  const [loading, setLoading] = useState(false);
  const [sampleDocs, setSampleDocs] = useState([]);

  // Configure AWS
  const configureAWS = () => {
    AWS.config.update({
      accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
      secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
      region: "ap-south-1",
    });
  };

  // Fetch sample documents from S3
  const fetchS3Files = async () => {
    try {
      setLoading(true);
      configureAWS();

      const s3 = new AWS.S3();
      const bucketName = "youvah-cpp-files";
      const folderPrefix = "Sample Docs/";

      const listParams = {
        Bucket: bucketName,
        Prefix: folderPrefix,
      };

      const listData = await s3.listObjectsV2(listParams).promise();

      // Filter out folder objects and process files
      const files = listData.Contents.filter((file) => file.Key !== folderPrefix && !file.Key.endsWith("/")).map((file) => {
        const fileName = file.Key.split("/").pop();
        const fileExtension = fileName.split(".").pop().toLowerCase();

        // Generate signed URL for the file
        const signedUrl = s3.getSignedUrl("getObject", {
          Bucket: bucketName,
          Key: file.Key,
          Expires: 60 * 60 * 24, // URL expires in 24 hours
        });

        // Create a user-friendly title based on filename
        let title = fileName.replace(/\.[^/.]+$/, ""); // Remove file extension
        title = title.replace(/[-_]/g, " "); // Replace dashes and underscores with spaces
        title = title.replace(/\b\w/g, (l) => l.toUpperCase()); // Capitalize first letter of each word

        return {
          title: title,
          src: signedUrl,
          download: signedUrl,
          key: file.Key,
          size: file.Size,
          lastModified: file.LastModified,
        };
      });

      setSampleDocs(files);
    } catch (error) {
      console.error("Error fetching S3 files:", error);
      message.error("Failed to load sample documents from server");

      // Fallback to local files if S3 fails
      setSampleDocs([
        { title: "Parent PAN card", src: "/samples/pan-sample.svg", download: "/samples/pan-sample.svg" },
        { title: "Parent Aadhaar card", src: "/samples/aadhaar-sample.svg", download: "/samples/aadhaar-sample.svg" },
        { title: "Student birth certificate", src: "/samples/birth-certificate-sample.svg", download: "/samples/birth-certificate-sample.svg" },
        { title: "Partner MOU", src: "/samples/MOU.pdf", download: "/samples/MOU.pdf" },
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchS3Files();
  }, []);

  const steps = [
    {
      title: "Registration",
      description: (
        <>
          Create a lead via the registration link from HOME page or add the lead manually from <Link href="/counsellor/leads">here</Link>.
        </>
      ),
    },
    {
      title: "Interviews (Optional)",
      description: "Interviews are typically required, but for this channel the interview requirement is waived.",
    },
    {
      title: "Passport Check",
      description: "Ensure that the student and at least one parent possess a valid passport with a minimum validity of 6 months from the date of travel.",
    },
    {
      title: "Payment Collection",
      description: (
        <>
          Collect payment in the designated account. Ensure the UTR includes your counsellor code: <Tag color="blue">[CounsellorCode]</Tag>.
        </>
      ),
    },
    {
      title: "Document Collection",
      description: "Collect and attach clear copies of the required documents. Samples are attached below",
    },
    {
      title: "Orientation",
      description: "Share the orientation link with the student/parent.",
    },
    {
      title: "Approval & Commission",
      description: "After admin approves documents and full payment is received, you become eligible for commission as per policy.",
    },
  ];

  const documents = ["Parent PAN card", "Parent Aadhaar card", "Student birth certificate"];

  return (
    <div style={{ padding: 24, background: "#f7f8fa", minHeight: "100%" }}>
      <Row justify="center">
        <Col xs={24} md={22} lg={20} xl={18}>
          <Card bordered style={{ borderRadius: 12, marginBottom: 16 }}>
            <Title level={3} style={{ margin: 0 }}>
              Student Onboarding Process
            </Title>
            <Paragraph type="secondary" style={{ marginTop: 8 }}>
              Follow these steps to onboard a student via the channel partner flow.
            </Paragraph>
          </Card>

          <Card bordered style={{ borderRadius: 12, marginBottom: 16 }}>
            <Steps direction="vertical" progressDot current={steps.length} items={steps} />
            <Card type="inner" title="Required documents (with samples)" style={{ marginTop: 16 }}>
              <List
                itemLayout="horizontal"
                dataSource={sampleDocs}
                renderItem={(doc) => (
                  <List.Item
                    actions={[
                      <a key="view" target="_blank" href={doc.download}>
                        View sample
                      </a>,
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<Image src={doc.src} width={120} height={75} style={{ objectFit: "cover", borderRadius: 8 }} preview />}
                      title={<Text strong>{doc.title}</Text>}
                      description={<Text type="secondary">Collect this document in a similar clear format.</Text>}
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Card>

          <Card bordered style={{ borderRadius: 12 }}>
            <Title level={4} style={{ marginTop: 0 }}>
              FAQs
            </Title>
            <Collapse bordered={false}>
              <Panel header="What if the student/parent does not have a passport?" key="1">
                <Paragraph>
                  Proceed with lead creation and advise them to initiate the passport application immediately in "tatkal mode". Timelines may depend on local
                  issuance.
                </Paragraph>
              </Panel>
              <Panel header="How should I share payment proof?" key="2">
                <Paragraph>
                  Share the UTR reference and include <Tag color="blue">[CounsellorCode]</Tag> in the remarks wherever possible. Ask for the screenshot/receipt
                  of the payment and upload it in the lead record.
                </Paragraph>
              </Panel>
              <Panel header="Where do I upload documents?" key="3">
                <Paragraph>
                  Attach the collected documents in the student’s lead record (e.g., via <Link href="/counsellor/leads">here</Link>) following the standard
                  upload flow.
                </Paragraph>
              </Panel>
              <Panel header="When is the commission released?" key="4">
                <Paragraph>After admin approves all documents and confirms full payment, as per the commission policy.</Paragraph>
              </Panel>
            </Collapse>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default AboutProcess;
